import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import {
  getAnchorEntitlements,
  type AnchorEntitlements,
} from "@/lib/entitlements/anchorsClient";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";

/**
 * Workspace-scoped anchor-entitlements context.
 *
 * Holds the org's anchor-key set (`turbo_sales`, `turbo_manufacturing`) so
 * bucket/mode visibility decisions can compose role gating with entitlement
 * gating per `docs/design/admin_split_and_entitlements.md` §4 + §6.3. One
 * provider sits high in the workspace shell so every bucket consumer reads
 * from cache; refetches automatically when the scoped org changes (org
 * switcher / impersonation).
 *
 * Loading semantics: `anchors === null` means "not loaded yet" — callers
 * MUST treat this as "open" (don't hide buckets) to avoid flicker. The
 * `canAccessByAnchor` helper in `modeAnchorRequirement.ts` encodes this
 * default.
 */

export type AnchorEntitlementsContextValue = {
  /** Loaded anchor set, or `null` while loading / when the fetch failed. */
  anchors: AnchorEntitlements | null;
  /** Initial load in progress. */
  loading: boolean;
  /** Background refresh in progress. */
  refreshing: boolean;
  /** Last fetch error message, if any. */
  error: string | null;
  /** Scoped organization id the loaded entitlements correspond to. */
  scopedOrganizationId: string | null;
  /** Force-refresh after an entitlement change (e.g. admin tooling toggle). */
  refresh: () => void;
};

const AnchorEntitlementsContext = createContext<AnchorEntitlementsContextValue | null>(null);

export function AnchorEntitlementsProvider(props: { children: ReactNode }) {
  const scopedOrganizationId = useScopedOrganizationId();
  const [anchors, setAnchors] = useState<AnchorEntitlements | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { refresh?: boolean }) => {
      if (!scopedOrganizationId) {
        setAnchors(null);
        setLoading(false);
        setRefreshing(false);
        setError(null);
        return;
      }
      if (opts?.refresh) setRefreshing(true);
      else {
        setLoading(true);
        setAnchors(null);
      }
      setError(null);

      const result = await getAnchorEntitlements();
      if (!result.ok) {
        setAnchors(null);
        setError(result.error);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      setAnchors(result.data.anchors);
      setLoading(false);
      setRefreshing(false);
    },
    [scopedOrganizationId]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const value: AnchorEntitlementsContextValue = {
    anchors,
    loading,
    refreshing,
    error,
    scopedOrganizationId,
    refresh: () => void load({ refresh: true }),
  };

  return (
    <AnchorEntitlementsContext.Provider value={value}>
      {props.children}
    </AnchorEntitlementsContext.Provider>
  );
}

export function useAnchorEntitlements(): AnchorEntitlementsContextValue {
  const ctx = useContext(AnchorEntitlementsContext);
  if (!ctx) {
    return {
      anchors: null,
      loading: false,
      refreshing: false,
      error: null,
      scopedOrganizationId: null,
      refresh: () => {},
    };
  }
  return ctx;
}
